const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const blobCache = new Map();
/** Dedupes concurrent fetches for the same file (e.g. company logo shown in sidebar + header). */
const inFlightFetches = new Map();

export function getAuthHeaders(extra = {}) {
  const headers = { ...extra };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** True when the value is an inline or external URL that needs no authenticated fetch. */
export function isDirectMediaSrc(value) {
  const url = String(value || '').trim();
  if (!url) return false;
  if (/^data:/i.test(url)) return true;
  if (/^https?:\/\//i.test(url) && !url.includes('/api/files/')) return true;
  return false;
}

/** Builds the fetch URL for a stored file reference (key or /api/files path). */
export function resolveMediaFetchUrl(value) {
  const url = String(value || '').trim();
  if (!url || isDirectMediaSrc(url)) {
    return '';
  }
  if (url.startsWith('/api/files/')) {
    return `${API_BASE_URL}${url}`;
  }
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }
  const encoded = url.split('/').map((segment) => encodeURIComponent(segment)).join('/');
  return `${API_BASE_URL}/api/files/${encoded}`;
}

/** @deprecated Use resolveMediaFetchUrl; kept for link href construction. */
export function resolveFileUrl(value) {
  const direct = String(value || '').trim();
  if (!direct) return '';
  if (isDirectMediaSrc(direct)) return direct;
  return resolveMediaFetchUrl(direct);
}

export async function fetchAuthenticatedBlobUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (isDirectMediaSrc(trimmed)) return trimmed;

  const fetchUrl = resolveMediaFetchUrl(trimmed);
  if (!fetchUrl) return '';

  const cached = blobCache.get(fetchUrl);
  if (cached) return cached;

  const pending = inFlightFetches.get(fetchUrl);
  if (pending) return pending;

  const promise = (async () => {
    const response = await fetch(fetchUrl, { headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error('Failed to load file');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    blobCache.set(fetchUrl, blobUrl);
    return blobUrl;
  })();

  inFlightFetches.set(fetchUrl, promise);
  try {
    return await promise;
  } finally {
    inFlightFetches.delete(fetchUrl);
  }
}

export async function openAuthenticatedFile(value) {
  const blobUrl = await fetchAuthenticatedBlobUrl(value);
  if (blobUrl) {
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  }
}

export function revokeMediaBlobCache() {
  blobCache.forEach((blobUrl) => {
    if (blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  });
  blobCache.clear();
  inFlightFetches.clear();
}

export function invalidateMediaBlobCacheFor(value) {
  const fetchUrl = resolveMediaFetchUrl(value);
  if (!fetchUrl) {
    return;
  }
  const cached = blobCache.get(fetchUrl);
  if (cached && cached.startsWith('blob:')) {
    URL.revokeObjectURL(cached);
  }
  blobCache.delete(fetchUrl);
  inFlightFetches.delete(fetchUrl);
}

export { API_BASE_URL };
